import { useState, useEffect, useCallback, useMemo } from "react";
import { Search, Trash2, Package, Plus, Download, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/calculations";
import { useIsMobile } from "@/components/custom/AppShell";

const DEFAULT_SUPPLIERS = ["Toolstation", "Screwfix", "Wickes", "B&Q", "Other"];

export default function MaterialsCatalogue() {
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [suppliers, setSuppliers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newItem, setNewItem] = useState({ supplier: "Toolstation", item_name: "", sku: "", unit_price: "", category: "Materials" });
  const [importing, setImporting] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortCol, setSortCol] = useState("item_name");
  const [sortDir, setSortDir] = useState("asc");

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchItems = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (supplierFilter && supplierFilter !== "all") params.set("supplier", supplierFilter);
      if (search) params.set("search", search);
      const resp = await api.get(`/materials-catalogue?${params.toString()}`);
      setItems(resp.data);
      // Build unique supplier list from all items
      if (!search && supplierFilter === "all") {
        const s = new Set(resp.data.map(i => i.supplier).filter(Boolean));
        setSuppliers(Array.from(s).sort());
      }
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [search, supplierFilter]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 300);
    return () => clearTimeout(t);
  }, [fetchItems]);

  // Initial load to get all suppliers
  useEffect(() => {
    api.get("/materials-catalogue").then(r => {
      const s = new Set(r.data.map(i => i.supplier).filter(Boolean));
      setSuppliers(Array.from(s).sort());
    }).catch(() => {});
  }, []);

  // Memoised filtered + sorted items (was inlined in JSX causing recompute every render)
  const visibleItems = useMemo(() => {
    return [...items]
      .filter(item => categoryFilter === "all" || item.category === categoryFilter)
      .sort((a, b) => {
        let av = a[sortCol] || "", bv = b[sortCol] || "";
        if (sortCol === "unit_price") { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
        else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
  }, [items, categoryFilter, sortCol, sortDir]);

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this item from the catalogue?")) return;
    try {
      await api.delete(`/materials-catalogue/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success("Item removed");
    } catch { toast.error("Failed to delete"); }
  };

  const handleAdd = async () => {
    if (!newItem.item_name.trim()) { toast.error("Item name is required"); return; }
    if (!newItem.supplier) { toast.error("Supplier is required"); return; }
    try {
      await api.post("/materials-catalogue", {
        supplier: newItem.supplier,
        item_name: newItem.item_name.trim(),
        sku: newItem.sku.trim(),
        unit_price: parseFloat(newItem.unit_price) || 0,
        category: newItem.category || "Materials",
      });
      toast.success("Material added to catalogue");
      setShowAdd(false);
      setNewItem({ supplier: "Toolstation", item_name: "", sku: "", unit_price: "", category: "Materials" });
      fetchItems();
      // Refresh supplier list
      api.get("/materials-catalogue").then(r => {
        const s = new Set(r.data.map(i => i.supplier).filter(Boolean));
        setSuppliers(Array.from(s).sort());
      }).catch(() => {});
    } catch { toast.error("Failed to add"); }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await api.post("/materials-catalogue/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${resp.data.imported} items${resp.data.skipped > 0 ? ` (${resp.data.skipped} skipped)` : ""}`);
      fetchItems();
      api.get("/materials-catalogue").then(r => {
        const s = new Set(r.data.map(i => i.supplier).filter(Boolean));
        setSuppliers(Array.from(s).sort());
      }).catch(() => {});
    } catch { toast.error("Import failed"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <div className={isMobile ? "px-4 pt-4 pb-3" : "px-8 pt-8 pb-4"}>
        <div className="flex items-center justify-between mb-6">
          <h1 className={isMobile ? "text-lg font-semibold" : "ui-page-title"}>Materials Catalogue</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-mono mr-2" style={{ color: "var(--text-muted)" }}>{items.length} items</span>
            <a href={`${API_URL}/api/materials-catalogue/template-csv`} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-secondary)" }} data-testid="download-template">
              <Download className="w-3.5 h-3.5" /> Template CSV
            </a>
            <a href={`${API_URL}/api/materials-catalogue/export-csv`} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-secondary)" }} data-testid="export-csv">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </a>
            <label className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition-colors ${importing ? "opacity-50" : ""}`} style={{ background: "var(--primary-light)", color: "var(--primary)" }} data-testid="import-csv">
              <Upload className="w-3.5 h-3.5" /> {importing ? "Importing..." : "Import CSV"}
              <input type="file" accept=".csv" className="hidden" onChange={handleImportCSV} disabled={importing} />
            </label>
            <Button onClick={() => setShowAdd(true)} className="btn-primary gap-1.5 h-9 px-4 text-sm" data-testid="add-catalogue-item">
              <Plus className="w-4 h-4" /> Add Material
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className={`flex ${isMobile ? "flex-col" : "items-center"} gap-3 mb-6`}>
          <div className={`relative ${isMobile ? "w-full" : "flex-1 max-w-sm"}`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by item name or SKU..."
              className="pl-10 h-10 rounded-lg" style={{ border: "1px solid var(--border)" }}
              data-testid="catalogue-search"
            />
          </div>
          <Select value={supplierFilter} onValueChange={setSupplierFilter}>
            <SelectTrigger className="h-10 rounded-lg w-48" style={{ border: "1px solid var(--border)" }} data-testid="catalogue-supplier-filter">
              <SelectValue placeholder="All Suppliers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Suppliers</SelectItem>
              {suppliers.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="h-10 rounded-lg w-40" style={{ border: "1px solid var(--border)" }} data-testid="catalogue-category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="Materials">Materials</SelectItem>
              <SelectItem value="Electrical">Electrical</SelectItem>
              <SelectItem value="Plumbing">Plumbing</SelectItem>
              <SelectItem value="Decorating">Decorating</SelectItem>
              <SelectItem value="Tools">Tools</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className={isMobile ? "px-4 pb-4" : "px-8 pb-8"}>
        {loading ? (
          <p className="text-center py-10" style={{ color: "var(--text-muted)" }}>Loading...</p>
        ) : items.length === 0 ? (
          <div className="ui-surface flex flex-col items-center py-16">
            <Package className="w-10 h-10 mb-4" style={{ color: "#9CA3AF" }} />
            <p style={{ fontSize: "15px", fontWeight: 600, color: "#374151" }}>
              {search || supplierFilter !== "all" || categoryFilter !== "all" ? "No items match your filters" : "No materials in catalogue"}
            </p>
            <p style={{ fontSize: "13px", color: "#6B7280", marginTop: "8px", textAlign: "center", maxWidth: "360px" }}>
              {search || supplierFilter !== "all" || categoryFilter !== "all" ? "Try adjusting your search or filters" : "Items are added automatically when you save materials on a quote, or add them manually"}
            </p>
            {!search && supplierFilter === "all" && categoryFilter === "all" && (
              <button onClick={() => setShowAdd(true)} className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold" style={{ background: "#E05C2A", color: "#fff" }}><Plus className="w-4 h-4" /> Add Material</button>
            )}
          </div>
        ) : isMobile ? (
          <div className="space-y-2">
            {items.map(item => (
              <div key={item.id} className="ui-surface p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{item.item_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="badge-pill badge-blue text-[10px]">{item.supplier}</span>
                      {item.sku && <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{item.sku}</span>}
                    </div>
                  </div>
                  <p className="text-sm font-mono font-bold flex-shrink-0">{formatCurrency(item.unit_price)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="ui-surface overflow-hidden">
            <table className="ui-table">
              <thead>
                <tr>
                  <th className="cursor-pointer select-none" onClick={() => { if (sortCol === "supplier") setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol("supplier"); setSortDir("asc"); } }}>Supplier {sortCol === "supplier" && (sortDir === "asc" ? "↑" : "↓")}</th>
                  <th className="cursor-pointer select-none" onClick={() => { if (sortCol === "item_name") setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol("item_name"); setSortDir("asc"); } }}>Item Name {sortCol === "item_name" && (sortDir === "asc" ? "↑" : "↓")}</th>
                  <th className="cursor-pointer select-none" onClick={() => { if (sortCol === "sku") setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol("sku"); setSortDir("asc"); } }}>SKU {sortCol === "sku" && (sortDir === "asc" ? "↑" : "↓")}</th>
                  <th>Category</th>
                  <th className="text-right cursor-pointer select-none" onClick={() => { if (sortCol === "unit_price") setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol("unit_price"); setSortDir("asc"); } }}>Unit Price {sortCol === "unit_price" && (sortDir === "asc" ? "↑" : "↓")}</th>
                  <th className="text-center cursor-pointer select-none" onClick={() => { if (sortCol === "updated_at") setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol("updated_at"); setSortDir("desc"); } }}>Updated {sortCol === "updated_at" && (sortDir === "asc" ? "↑" : "↓")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map(item => (
                  <tr key={item.id}>
                    <td><span className="badge-pill badge-blue text-[10px]">{item.supplier}</span></td>
                    <td className="font-medium">{item.item_name}</td>
                    <td className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                      {item.sku ? (
                        item.sku.length > 20 ? (
                          <span title={item.sku}>{item.sku.slice(0, 20)}...</span>
                        ) : item.sku
                      ) : "—"}
                    </td>
                    <td className="text-xs" style={{ color: "var(--text-secondary)" }}>{item.category || "—"}</td>
                    <td className="text-right font-mono font-bold">{formatCurrency(item.unit_price)}</td>
                    <td className="text-center text-xs" style={{ color: "var(--text-muted)" }}>{item.updated_at ? new Date(item.updated_at).toLocaleDateString("en-GB") : "—"}</td>
                    <td>
                      <button onClick={() => handleDelete(item.id)} className="p-1 rounded hover:bg-red-50 transition-colors" style={{ color: "var(--text-muted)" }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Material Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-catalogue-dialog">
          <DialogHeader><DialogTitle>Add Material to Catalogue</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium uppercase tracking-wide mb-1.5 block" style={{ color: "var(--text-muted)" }}>Supplier *</Label>
              <Select value={newItem.supplier} onValueChange={(v) => setNewItem(p => ({ ...p, supplier: v }))}>
                <SelectTrigger className="h-10 rounded-lg" style={{ border: "1px solid var(--border)" }}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[...new Set([...DEFAULT_SUPPLIERS, ...suppliers])].sort().map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-wide mb-1.5 block" style={{ color: "var(--text-muted)" }}>Item Name *</Label>
              <Input value={newItem.item_name} onChange={(e) => setNewItem(p => ({ ...p, item_name: e.target.value }))} placeholder="e.g. 10mm Copper Pipe" className="h-10 rounded-lg" style={{ border: "1px solid var(--border)" }} data-testid="new-cat-item-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium uppercase tracking-wide mb-1.5 block" style={{ color: "var(--text-muted)" }}>SKU</Label>
                <Input value={newItem.sku} onChange={(e) => setNewItem(p => ({ ...p, sku: e.target.value }))} placeholder="e.g. TST-001" className="h-10 rounded-lg font-mono" style={{ border: "1px solid var(--border)" }} data-testid="new-cat-sku" />
              </div>
              <div>
                <Label className="text-xs font-medium uppercase tracking-wide mb-1.5 block" style={{ color: "var(--text-muted)" }}>Unit Price</Label>
                <Input type="number" step="0.01" value={newItem.unit_price} onChange={(e) => setNewItem(p => ({ ...p, unit_price: e.target.value }))} placeholder="0.00" className="h-10 rounded-lg font-mono" style={{ border: "1px solid var(--border)" }} data-testid="new-cat-price" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-wide mb-1.5 block" style={{ color: "var(--text-muted)" }}>Category</Label>
              <Select value={newItem.category} onValueChange={(v) => setNewItem(p => ({ ...p, category: v }))}>
                <SelectTrigger className="h-10 rounded-lg" style={{ border: "1px solid var(--border)" }}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Materials">Materials</SelectItem>
                  <SelectItem value="Electrical">Electrical</SelectItem>
                  <SelectItem value="Plumbing">Plumbing</SelectItem>
                  <SelectItem value="Decorating">Decorating</SelectItem>
                  <SelectItem value="Tools">Tools</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} className="btn-primary gap-1" data-testid="save-cat-item">
              <Plus className="w-4 h-4" /> Add to Catalogue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
